import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import { useMemo } from 'react'

import { isNoneChartMode } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'
import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'

import { calcControlsProps } from '@aytm/app/stats/page/question-views/answer-variants/lib/props/controls'
import { buildOnCommentsClick, calcSortOrder } from '@aytm/app/stats/page/question-views/oe/lib/table-utils'
import { calcChartProps } from '@aytm/app/stats/page/question-views/sft/lib/props/chart'
import { buildBatchProps, calcBatches } from '@aytm/app/stats/page/question-views/sft/lib/props/batches'
import { calcTableProps } from '@aytm/app/stats/page/question-views/sft/lib/table-utils'
import useRapidAssociationState from '@aytm/app/stats/page/question-views/sft/states/useRapidAssociationState'

import Items from '@aytm/app/stats/page/question-views/mx/components/items/Items'
import Chart from '@aytm/app/stats/page/question-views/sft/components/chart/Chart'
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'
import Controls from '@aytm/app/stats/page/question-views/components/controls/Controls'
import Table from '@aytm/app/stats/components/data-views/table/components/Table'
import ViewControls from '@aytm/app/stats/page/question-views/sft/components/Controls'
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'

// The rapid-association block -- what a word-association question draws. A strip of controls, then,
// under a picker naming which of the three views to draw, the chart over the words. Beneath that the
// words as a table, or -- in the stacked view -- the words as a list, each opening on how it was
// answered. A rapid-association question is never word-cloud-eligible, as on master.
//
// block.coffee owns the mount, the report-param write and the payload. Everything the researcher
// sees and moves is here, and the block answers for its whole subtree through the api it hands up.
const STACKED_VIEW_MODE = 'stacked'

const Block = props => {
  const {
    id,
    questionId,
    questionKey,
    type,
    btnsType,
    format,
    components,
    answer,
    answerVariants,
    clickable,
    link,
    isReportXpert,
    questionChartMode,
    defaultSortOrder,
    labelTarget,
    handlers,
    onReady,
    onStateChange
  } = props

  // the words the list draws are the batches the exporters and a row click read, so all three take this one list
  const batches = useMemo(() => calcBatches({ answerVariants, btnsType }), [answerVariants, btnsType])

  const {
    viewMode,
    chartMode,
    zoomLevel,
    sortOrder,
    uncollapsed,
    onSortChange,
    onChartReady,
    onViewModeChange,
    onChartModeChange,
    onZoomChange,
    onBatchReady,
    onBatchStateChange,
    onToggle
  } = useRapidAssociationState({
    params: props.params,
    paramsRevision: props.paramsRevision,
    questionKey,
    type,
    format,
    answer,
    answerVariants,
    batches,
    components,
    questionChartMode,
    defaultSortOrder,
    labelTarget,
    onReady,
    onStateChange,
    onBatchParamsChange: props.onBatchParamsChange
  })

  const question = { ...props, viewMode, chartMode, zoomLevel, sortOrder, uncollapsed }

  const chartHandlers = useMemo(
    () => ({
      ...handlers,
      onChartModeChange,
      onZoomChange,
      // the chart hands its api to both sides: the shell serializes the svg, the state reads the axis
      onChartReady: api => {
        onChartReady(api)
        handlers?.onChartReady?.(api)
      }
    }),
    [handlers, onChartModeChange, onZoomChange, onChartReady]
  )

  const chartProps = calcChartProps({ ...question, handlers: chartHandlers })

  // the comments panel is opened from here, not by the shell: the identity it addresses the
  // question by is fixed, and the word the click names is looked up in the variants
  const tableHandlers = useMemo(() => {
    const open = buildOnCommentsClick({ id, questionId, link, isReportXpert })

    return {
      ...handlers,
      // a clicked column names the order it wants; the state keeps it, so the report remembers it
      onSort: (colIndex, dir, cols) => {
        const order = calcSortOrder(cols, colIndex, dir)
        if (order) onSortChange(order)
      },
      onCommentsClick: (event, position) => open(event, position, answerVariants)
    }
  }, [handlers, id, questionId, link, isReportXpert, answerVariants, onSortChange])

  const isStacked = STACKED_VIEW_MODE === viewMode

  // Every word of the stacked view is a question of its own as far as the report params go, so it
  // announces its own changes and this block forwards them to the shell.
  const batchPropsOf = useMemo(
    () =>
      buildBatchProps({
        questionId: id,
        questionKey,
        type,
        format,
        components,
        answer,
        defaultSortOrder,
        batchParams: props.batchParams,
        handlers: {
          ...handlers,
          onBatchReady,
          onBatchStateChange,
          onBatchWcParamsChange: props.onBatchWcParamsChange
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      id,
      questionKey,
      type,
      format,
      components,
      answer,
      defaultSortOrder,
      handlers,
      onBatchReady,
      onBatchStateChange,
      props.batchParams,
      props.onBatchWcParamsChange
    ]
  )

  // the stacked view closes the block with its batch list, so the response count sits under that
  // rather than under the chart; every other view carries the count itself
  const itemsOn = isStacked && isComponentOn(components, 'subQuestions')

  return (
    <>
      <BlockWrapper {...props}>
        <Controls {...calcControlsProps({ ...question, handlers })} />

        <>
          {isComponentOn(components, 'controls') && (
            <ViewControls
              features={props.features}
              viewMode={viewMode}
              btnsType={btnsType}
              onModelChange={change => onViewModeChange(change.viewMode)}
            />
          )}

          {isComponentOn(components, 'chart') &&
            !isNoneChartMode(chartProps.questionChartMode, chartProps.globalChartMode) && (
              <ChartPlaceholder isLoading={'loading' === answer}>
                <Chart {...chartProps} />
              </ChartPlaceholder>
            )}

          {isComponentOn(components, 'table') && !isStacked && (
            <Table
              {...calcTableProps({
                id,
                type,
                format,
                components,
                answer,
                answerVariants,
                viewMode,
                sortOrder,
                clickable,
                handlers: tableHandlers
              })}
            />
          )}
        </>
      </BlockWrapper>

      {itemsOn && (
        <BlockWrapper answer={answer}>
          <Items
            questionId={id}
            batches={batches}
            questionType={type}
            components={components}
            uncollapsed={uncollapsed}
            batchPropsOf={batchPropsOf}
            onToggle={onToggle}
          />
        </BlockWrapper>
      )}
    </>
  )
}

Block.displayName = 'RapidAssociationBlock'

// What the shell hands in: the question, the sections of the report params the block owns, the
// params every word of the stacked view owns, and the handlers the surfaces report back through.
export const BLOCK_PROP_TYPES = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  type: PropTypes.string,
  variant: PropTypes.string,
  questionType: PropTypes.string,
  btnsType: PropTypes.string,
  format: PropTypes.object,
  components: PropTypes.object,
  answer: answerPropType,
  answerVariants: PropTypes.array,
  answerType: PropTypes.string,
  disabled: PropTypes.bool,
  clickable: PropTypes.bool,
  questionChartMode: PropTypes.string,
  defaultSortOrder: PropTypes.string,
  features: PropTypes.object,
  params: PropTypes.shape({
    tcv: PropTypes.string,
    cm: PropTypes.string,
    z: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    s: PropTypes.string,
    cb: PropTypes.string,
    wc: PropTypes.string
  }),
  // bumped by the shell every time it re-reads the params off the model
  paramsRevision: PropTypes.number,
  labelTarget: PropTypes.object,
  link: PropTypes.string,
  isReportXpert: PropTypes.bool,
  modelDefaults: PropTypes.object,
  // the report params every word of the stacked view owns, keyed by position
  batchParams: PropTypes.object,
  handlers: PropTypes.object,
  onReady: PropTypes.func,
  onStateChange: PropTypes.func,
  onBatchParamsChange: PropTypes.func,
  onBatchWcParamsChange: PropTypes.func
}

Block.propTypes = BLOCK_PROP_TYPES

export default Block
